import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Observer } from 'mobx-react';
import { useTheme } from '@mui/material/styles';
import { IconButton, Slider, ToggleButton, ToggleButtonGroup, Tooltip as MuiTooltip } from '@mui/material';
import ScatterPlotIcon from '@mui/icons-material/ScatterPlot';
import ViewTimelineOutlinedIcon from '@mui/icons-material/ViewTimelineOutlined';
import ZoomInOutlinedIcon from '@mui/icons-material/ZoomInOutlined';
import ZoomOutOutlinedIcon from '@mui/icons-material/ZoomOutOutlined';
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined';

import { useStyles } from './ExecutionSummaryGraph.styles'

import { useExecutionsUIStore } from '@core/useStores';
import { messages } from 'NeoAnalytics/neoAnalytics.messages';
import { EventTimeline } from '../EventTimeline';

import { ThemeProps } from '@styles/theme';
import {
    ResponsiveContainer,
    XAxis,
    YAxis,
    CartesianGrid,
    Scatter,
    Customized,
    ScatterChart,
    Tooltip
} from 'recharts';
import { IdxGroupClickInfo, ZoneLabelInfo, ZonesOverlayVertical } from '@shared/components/ZoneAxis';
import { EventScatterActiveShape, EventScatterShape, resolveTypeMeta, ScatterEvent } from '../EventScatterShape';
import { CrosshairCursor, EventScatterTooltip } from '../EventScatterTooltip';
import CustomHorizontalScroller from '../CustomScrollbar/CustomHorizontalScroller';

type SelectedScatterPoint = {
    event: ScatterEvent;
    point: {
        cx: number;
        cy: number;
    };
};

type TooltipPosition = {
    left: number;
    top: number;
};

type SelectedStepGroup = {
    zones: ZoneLabelInfo[];
    point: {
        x: number;
        y: number;
    };
};

function typeFormatter(value: string | undefined): string {
    const cutLabel = value ? value.split('_')[0] : '';
    const label = cutLabel ? String(cutLabel?.toLowerCase()).charAt(0).toUpperCase() + String(cutLabel?.toLowerCase()).slice(1) : 'Generic'

    return label;
}

const DEFAULT_TICK_STEP = 10;
const DEFAULT_PX_PER_SECOND = 24 * 3 / 10;
const MERGE_PROXIMITY_PX = 20;

const ZOOM_MARKS = [1, 2, 3, 5];
function snapToMark(value: number): number {
    return ZOOM_MARKS.reduce((nearest, mark) =>
        Math.abs(mark - value) < Math.abs(nearest - value) ? mark : nearest
    );
}

function stepMark(value: number, direction: 1 | -1): number {
    const currentIndex = ZOOM_MARKS.indexOf(snapToMark(value));
    const nextIndex = Math.min(Math.max(currentIndex + direction, 0), ZOOM_MARKS.length - 1);

    return ZOOM_MARKS[nextIndex];
}

function buildMergedLabel(events: ScatterEvent[]): string {
    const distinctLabels = Array.from(new Set(events.map(event => event.label).filter(Boolean)));

    if (distinctLabels.length <= 2) {
        return distinctLabels.join(' - ');
    }

    return `${distinctLabels.slice(0, 2).join(' - ')} +${distinctLabels.length - 2}`;
}

function buildMergedDescription(events: ScatterEvent[]): string {
    if (events.length === 1) {
        return events[0].description;
    }

    const distinctDescriptions = Array.from(new Set(events.map(event => event.description).filter(Boolean)));

    if (distinctDescriptions.length === 1) {
        return `${events.length} merged events: ${distinctDescriptions[0]}`;
    }

    return `${events.length} merged events`;
}

function mergeCloseEvents(events: ScatterEvent[] | undefined, proximitySeconds: number): ScatterEvent[] {
    if (!events?.length) {
        return [];
    }

    const groupedByType = events.reduce<Record<string, ScatterEvent[]>>((acc, event) => {
        acc[event.type] ??= [];
        acc[event.type].push(event);

        return acc;
    }, {});

    const mergedEvents = Object.values(groupedByType).flatMap(group => {
        const orderedGroup = [...group].sort((left, right) => left.time - right.time);
        const clusters: ScatterEvent[][] = [];

        orderedGroup.forEach(event => {
            const currentCluster = clusters[clusters.length - 1];
            if (!currentCluster) {
                clusters.push([event]);

                return;
            }

            const previousEvent = currentCluster[currentCluster.length - 1];
            if (event.time - previousEvent.time < proximitySeconds) {
                currentCluster.push(event);

                return;
            }

            clusters.push([event]);
        });

        return clusters.map(cluster => {
            if (cluster.length === 1) {
                return cluster[0];
            }

            const averageTime = cluster.reduce((sum, event) => sum + event.time, 0) / cluster.length;

            return {
                ...cluster[0],
                time: averageTime,
                label: buildMergedLabel(cluster),
                description: buildMergedDescription(cluster),
                value: {
                    EventCount: cluster.length,
                    Labels: Array.from(new Set(cluster.map(event => event.label).filter(Boolean))).join(', '),
                    Times: cluster.map(event => event.time),
                },
                mergedCount: cluster.length,
                mergedItems: cluster,
            };
        });
    });

    return mergedEvents.sort((left, right) => left.time - right.time);
}

const typeMetaLight = {
    ERROR: { color: "#B42318", label: "Error" },
    PROCEDURE: { color: "#14B8D4", label: "Procedure" },
    INTERACTION: { color: "#5B3CC4", label: "Interaction" },
    WARNING: { color: "#F59E0B", label: "Warning" },
    INFO: { color: "#2563EB", label: "Info" },
};

const typeMetaDark = {
    ERROR: { color: "#ff6b6b", label: "Error" },
    PROCEDURE: { color: "#38bdf8", label: "Procedure" },
    INTERACTION: { color: "#a78bfa", label: "Interaction" },
    WARNING: { color: "#fbbf24", label: "Warning" },
    INFO: { color: "#60a5fa", label: "Info" },
};

function EventTypeTick({ x = 0, y = 0, payload, typeMeta }: { x?: number; y?: number; payload?: { value?: string }; typeMeta?: typeof typeMetaLight }) {
    const value = payload?.value;
    const meta = resolveTypeMeta(value ?? '', typeMeta);

    return (
        <text x={x} y={y} dy={5} textAnchor="end" fill={meta.color} fontSize={16} fontWeight={600}>
            {typeFormatter(value)}
        </text>
    );
}

export function timeFormatterDuration(value: number | undefined): string {
    const s = Math.max(0, Math.floor(value!));

    const hours = Math.floor(s / 3600);
    const minutes = Math.floor((s % 3600) / 60);
    const seconds = s % 60;

    const pad2 = (n: number) => String(n).padStart(2, "0");

    return hours > 0 ? `${pad2(hours)}:${pad2(minutes)}:${pad2(seconds)}` : `${pad2(minutes)}:${pad2(seconds)}`;
}

export function getWidestString(
    strings: string[],
    fontSize: number,
    options?: {
        fontFamily?: string;
        fontWeight?: string;
        fontStyle?: string;
    }
): number | null {
    if (!strings.length) {return null;}

    const theme = useTheme<ThemeProps>();

    const fontFamily = theme.font.primary;
    const fontWeight = options?.fontWeight ?? "normal";
    const fontStyle = options?.fontStyle ?? "normal";

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) {return null;}

    ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}`;

    let widestText = strings[0];
    let maxWidth = ctx.measureText(widestText).width;

    for (let i = 1; i < strings.length; i++) {
        const w = ctx.measureText(strings[i]).width;
        if (w > maxWidth) {
            maxWidth = w;
            widestText = strings[i];
        }
    }

    return maxWidth;
}

export const ExecutionSummaryGraph: React.FC = () =>{
    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });
    const typeMeta = theme.isDark ? typeMetaDark : typeMetaLight;
    const [viewMode, setViewMode] = useState<'graph' | 'timeline'>('graph');
    const [zoomLevel, setZoomLevel] = useState(1);
    const [chartAreaWidth, setChartAreaWidth] = useState(0);
    const [selectedPoint, setSelectedPoint] = useState<SelectedScatterPoint | null>(null);
    const [hoveredEventKey, setHoveredEventKey] = useState<string | null>(null);
    const [selectedTooltipPosition, setSelectedTooltipPosition] = useState<TooltipPosition>({ left: 12, top: 12 });
    const [selectedStepGroup, setSelectedStepGroup] = useState<SelectedStepGroup | null>(null);
    const [stepGroupPopoverPosition, setStepGroupPopoverPosition] = useState<TooltipPosition>({ left: 12, top: 12 });
    const [highlightedZoneId, setHighlightedZoneId] = useState<string | null>(null);
    const graphContainerRef = useRef<HTMLDivElement | null>(null);
    const rightAreaObserverRef = useRef<ResizeObserver | null>(null);
    const chartViewportRef = useRef<HTMLDivElement | null>(null);
    const selectedTooltipRef = useRef<HTMLDivElement | null>(null);
    const stepGroupPopoverRef = useRef<HTMLDivElement | null>(null);

    const executionsUIStore = useExecutionsUIStore();

    const setRightAreaRef = useCallback((el: HTMLDivElement | null) => {
        rightAreaObserverRef.current?.disconnect();
        rightAreaObserverRef.current = null;
        if (!el) {return;}
        const update = () => setChartAreaWidth(el.offsetWidth);
        update();
        rightAreaObserverRef.current = new ResizeObserver(update);
        rightAreaObserverRef.current.observe(el);
    }, []);

    useEffect(() => {
        if (!selectedPoint) {
            return;
        }

        const handlePointerDown = (event: MouseEvent | TouchEvent) => {
            const target = event.target as Node | null;

            if (target && selectedTooltipRef.current?.contains(target)) {
                return;
            }

            setSelectedPoint(null);
        };

        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('touchstart', handlePointerDown);

        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('touchstart', handlePointerDown);
        };
    }, [selectedPoint]);

    useEffect(() => {
        if (!selectedStepGroup) {
            setHighlightedZoneId(null);

            return;
        }

        const handlePointerDown = (event: MouseEvent | TouchEvent) => {
            const target = event.target as Node | null;

            if (target && stepGroupPopoverRef.current?.contains(target)) {
                return;
            }

            setSelectedStepGroup(null);
        };

        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('touchstart', handlePointerDown);

        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('touchstart', handlePointerDown);
        };
    }, [selectedStepGroup]);

    useLayoutEffect(() => {
        if (!selectedStepGroup || !stepGroupPopoverRef.current || !chartViewportRef.current) {
            return;
        }

        const popoverBounds = stepGroupPopoverRef.current.getBoundingClientRect();
        const viewportBounds = chartViewportRef.current.getBoundingClientRect();
        const gap = 8;

        const left = Math.min(
            Math.max(selectedStepGroup.point.x - popoverBounds.width / 2, gap),
            Math.max(viewportBounds.width - popoverBounds.width - gap, gap),
        );
        const top = Math.max(selectedStepGroup.point.y - popoverBounds.height - gap, gap);

        setStepGroupPopoverPosition({ left, top });
    }, [selectedStepGroup]);

    useLayoutEffect(() => {
        if (!selectedPoint || !selectedTooltipRef.current || !chartViewportRef.current) {
            return;
        }

        const tooltipBounds = selectedTooltipRef.current.getBoundingClientRect();
        const viewportBounds = chartViewportRef.current.getBoundingClientRect();
        const gap = 14;

        const preferredLeft = selectedPoint.point.cx + 18;
        const fallbackLeft = selectedPoint.point.cx - tooltipBounds.width - 18;
        const left = preferredLeft + tooltipBounds.width + gap <= viewportBounds.width
            ? preferredLeft
            : fallbackLeft >= gap
                ? fallbackLeft
                : Math.min(Math.max(preferredLeft, gap), Math.max(viewportBounds.width - tooltipBounds.width - gap, gap));

        const preferredTop = selectedPoint.point.cy - tooltipBounds.height / 2;
        const top = Math.min(
            Math.max(preferredTop, gap),
            Math.max(viewportBounds.height - tooltipBounds.height - gap, gap),
        );

        setSelectedTooltipPosition({ left, top });
    }, [selectedPoint]);

    return (
        <Observer>
            {() => {
                const data = executionsUIStore.formattedExecution;
                const exeDuration = data?.exeDuration ?? 0;
                const basePxPerSecond = chartAreaWidth > 0 && exeDuration > 0
                    ? chartAreaWidth / exeDuration
                    : DEFAULT_PX_PER_SECOND;
                const pxPerSecond = basePxPerSecond * zoomLevel;
                const mergeProximity = MERGE_PROXIMITY_PX / pxPerSecond;
                const displayEvents = mergeCloseEvents(data?.formattedEvents as ScatterEvent[] | undefined, mergeProximity);
                const eventTypes = data?.eventTypes ?? [];
                const plotWidth = exeDuration * pxPerSecond;
                const formattedTypes = eventTypes.map(t => typeFormatter(t));
                const maxLabelWidth = getWidestString(formattedTypes!, 16);
                // Dynamic tick step so labels don't overlap (min ~55px per label)
                const MIN_TICK_PX = 55;
                const rawPxPerDefaultTick = plotWidth * DEFAULT_TICK_STEP / Math.max(exeDuration, 1);
                const tickMultiplier = Math.max(1, Math.ceil(MIN_TICK_PX / rawPxPerDefaultTick));
                const tickStep = DEFAULT_TICK_STEP * tickMultiplier;
                const summaryTicks = Array.from({ length: Math.floor(exeDuration / tickStep) + 1 }, (_, index) => index * tickStep);

                const onPointSelect = (event: ScatterEvent, point: { cx: number; cy: number }) => {
                    setSelectedPoint(current => {
                        const isSamePoint = current
                            && current.event.time === event.time
                            && current.event.type === event.type
                            && current.event.label === event.label;

                        if (isSamePoint) {
                            return null;
                        }

                        return { event, point };
                    });
                };

                const getEventKey = (event?: ScatterEvent | null) => event
                    ? `${event.type}-${event.time}-${event.label}-${event.parentEventId ?? ''}-${event.mergedCount ?? 1}`
                    : null;

                const updateHoveredPoint = (chartState: any) => {
                    const activeEvent = chartState?.activePayload?.[0]?.payload as ScatterEvent | undefined;
                    if (!activeEvent) {
                        setHoveredEventKey(null);

                        return;
                    }
                    setHoveredEventKey(getEventKey(activeEvent));
                };

                if(displayEvents.length > 0){
                    return (
                        <div className={styles.graphContainer} ref={graphContainerRef}>
                            <div className={styles.headerWraper}>
                                <span>Events</span>
                                <div className={styles.headerControls}>
                                    {viewMode === 'graph' && (
                                        <div className={styles.zoomControls}>
                                            <MuiTooltip title={messages['executionSummaryGraph.zoom.out']} arrow>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => setZoomLevel(stepMark(zoomLevel, -1))}
                                                        disabled={zoomLevel <= ZOOM_MARKS[0]}
                                                        sx={{ color: theme.colors.violet_primary }}
                                                    >
                                                        <ZoomOutOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </MuiTooltip>
                                            <div className={styles.zoomSliderWrap}>
                                                <Slider
                                                    value={zoomLevel}
                                                    min={1}
                                                    max={5}
                                                    step={0.1}
                                                    onChange={(_, value) => setZoomLevel(value as number)}
                                                    onChangeCommitted={(_, value) => setZoomLevel(snapToMark(value as number))}
                                                    marks={[
                                                        { value: 1 },
                                                        { value: 2 },
                                                        { value: 3 },
                                                        { value: 5 },
                                                    ]}
                                                    sx={{
                                                        color: theme.colors.violet_primary,
                                                        height: 3,
                                                        padding: '10px 0',
                                                        '& .MuiSlider-thumb': {
                                                            width: 12,
                                                            height: 12,
                                                            '&:hover': {
                                                                boxShadow: `0 0 0 5px ${theme.colors.violet_primary}22`,
                                                            },
                                                        },
                                                        '& .MuiSlider-rail': {
                                                            opacity: 0.2,
                                                        },
                                                        '& .MuiSlider-mark': {
                                                            width: 6,
                                                            height: 6,
                                                            borderRadius: '50%',
                                                            backgroundColor: theme.colors.violet_primary,
                                                            opacity: 0.35,
                                                            transform: 'translate(-50%, -50%)',
                                                        },
                                                        '& .MuiSlider-markActive': {
                                                            opacity: 0.9,
                                                            backgroundColor: '#fff',
                                                        },
                                                    }}
                                                />
                                            </div>
                                            <MuiTooltip title={messages['executionSummaryGraph.zoom.in']} arrow>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => setZoomLevel(stepMark(zoomLevel, 1))}
                                                        disabled={zoomLevel >= ZOOM_MARKS[ZOOM_MARKS.length - 1]}
                                                        sx={{ color: theme.colors.violet_primary }}
                                                    >
                                                        <ZoomInOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </MuiTooltip>
                                            <span className={styles.zoomReadout}>
                                                {zoomLevel.toFixed(1)}×
                                            </span>
                                            <MuiTooltip title={messages['executionSummaryGraph.zoom.reset']} arrow>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => setZoomLevel(1)}
                                                        disabled={zoomLevel === 1}
                                                        sx={{ color: theme.colors.violet_primary }}
                                                    >
                                                        <RestartAltOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </MuiTooltip>
                                        </div>
                                    )}
                                    <ToggleButtonGroup
                                        exclusive
                                        size="small"
                                        value={viewMode}
                                        onChange={(_, value) => value && setViewMode(value)}
                                        sx={{
                                            '& .MuiToggleButton-root': {
                                                width: 32,
                                                height: 32,
                                                padding: 0,
                                                color: theme.colors.grey_darker,
                                                borderColor: theme.colors.grey_darker,
                                                '&.Mui-selected': {
                                                    backgroundColor: theme.colors.violet_primary,
                                                    color: '#fff',
                                                    borderColor: theme.colors.violet_primary,
                                                    '&:hover': {
                                                        backgroundColor: theme.colors.violet_primary,
                                                    },
                                                },
                                            },
                                        }}
                                    >
                                        <ToggleButton value="graph" aria-label={messages['executionSummaryGraph.view.graph']}>
                                            <MuiTooltip title={messages['executionSummaryGraph.view.graph']} arrow>
                                                <ScatterPlotIcon fontSize="small" />
                                            </MuiTooltip>
                                        </ToggleButton>
                                        <ToggleButton value="timeline" aria-label={messages['executionSummaryGraph.view.timeline']}>
                                            <MuiTooltip title={messages['executionSummaryGraph.view.timeline']} arrow>
                                                <ViewTimelineOutlinedIcon fontSize="small" />
                                            </MuiTooltip>
                                        </ToggleButton>
                                    </ToggleButtonGroup>
                                </div>
                            </div>
                            {viewMode === 'timeline' ? (
                                <EventTimeline events={data?.formattedEvents as ScatterEvent[] | undefined} typeMeta={typeMeta} />
                            ) : (
                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: `${maxLabelWidth! + 50}px 1fr`,
                                        width: "100%",
                                        height: 'calc(100% - 50px)',
                                    }}
                                >
                                    <div style={{ height: "100%", overflow: "hidden", borderRight: "1px solid rgba(0,0,0,0.08)"}}>
                                        <div style={{
                                            height: 'calc(100% - 10px)',
                                        }}>
                                            <ResponsiveContainer width="100%" height="100%">
                                                <ScatterChart data={displayEvents} syncId="event-scatter-sync" margin={{ left: 0, right: 0, top: 50, bottom: 50 }}>
                                                    <XAxis xAxisId="time" dataKey="time" type="number" domain={[0, exeDuration]} hide />
                                                    <YAxis
                                                        yAxisId="type"
                                                        type="category"
                                                        dataKey="type"
                                                        ticks={eventTypes}
                                                        interval={0}
                                                        reversed
                                                        width={maxLabelWidth! + 50}
                                                        padding={{ top: 30, bottom: 30 }}
                                                        tickFormatter={typeFormatter}
                                                        tick={<EventTypeTick typeMeta={typeMeta} />}
                                                        strokeOpacity={0}
                                                        allowDuplicatedCategory={false}
                                                    />
                                                </ScatterChart>
                                            </ResponsiveContainer>
                                        </div>
                                        <div style={{
                                            borderTop: `1px solid ${theme.colors.grey_darker}`,
                                            height: 10,
                                        }}/>
                                    </div>

                                    <div ref={setRightAreaRef} style={{ height: '100%', overflow: 'hidden' }}>
                                        <CustomHorizontalScroller plotWidth={plotWidth} theme={theme} barHeight={10} border>
                                            {({ renderWidth }) => (
                                                <div className={styles.chartViewport} ref={chartViewportRef}>
                                                    <ResponsiveContainer width={renderWidth} height="100%">
                                                        <ScatterChart
                                                            data={displayEvents}
                                                            syncId="event-scatter-sync"
                                                            margin={{ left: 0, right: 0, top: 0, bottom: 50 }}
                                                            onMouseMove={updateHoveredPoint}
                                                            onMouseLeave={() => {
                                                                setHoveredEventKey(null);
                                                            }}
                                                        >
                                                            <CartesianGrid stroke={theme.colors.grey_darker} vertical={false} opacity={0.35} strokeDasharray={5}/>

                                                            <XAxis
                                                                name="time"
                                                                xAxisId="time"
                                                                dataKey="time"
                                                                type="number"
                                                                orientation="top"
                                                                domain={[0, exeDuration]}
                                                                ticks={summaryTicks}
                                                                interval={0}
                                                                tickCount={summaryTicks.length}
                                                                padding={{ left: 0, right: 0 }}
                                                                tickFormatter={timeFormatterDuration}
                                                                height={50}
                                                                tickMargin={10}
                                                                strokeOpacity={0.35}
                                                                allowDataOverflow
                                                            />

                                                            <YAxis
                                                                yAxisId="type"
                                                                type="category"
                                                                dataKey="type"
                                                                ticks={eventTypes}
                                                                interval={0}
                                                                reversed
                                                                hide
                                                                width={0}
                                                                padding={{ top: 30, bottom: 30 }}
                                                                tickFormatter={typeFormatter}
                                                                strokeOpacity={0}
                                                                allowDuplicatedCategory={false}
                                                            />

                                                            <Customized
                                                                component={(p: unknown) => (
                                                                    <ZonesOverlayVertical
                                                                        {...(p as any)}
                                                                        zones={data?.formattedAxisSteps}
                                                                        xAxisId="time"
                                                                        bottomLabelYPad={25}
                                                                        showFill={true}
                                                                        fontSize={16}
                                                                        oddFill={theme.colors.grey_light}
                                                                        evenFill={theme.colors.grey_lighter}
                                                                        fillOpacity={0.5}
                                                                        strokeWidth={0.5}
                                                                        showIdx
                                                                        hideLabel
                                                                        highlightedZoneId={highlightedZoneId}
                                                                        onIdxGroupClick={(group: IdxGroupClickInfo) => {
                                                                            setSelectedStepGroup({
                                                                                zones: group.zones,
                                                                                point: { x: group.x, y: group.y },
                                                                            });
                                                                        }}
                                                                    />
                                                                )}
                                                            />

                                                            <Tooltip
                                                                content={<EventScatterTooltip typeMeta={typeMeta} />}
                                                                cursor={
                                                                    <CrosshairCursor
                                                                        timeFormatter={timeFormatterDuration}
                                                                        lineDash="3 3"
                                                                        typeMeta={typeMeta}
                                                                    />
                                                                }
                                                            />

                                                            <Scatter
                                                                xAxisId="time"
                                                                yAxisId="type"
                                                                data={displayEvents}
                                                                shape={(shapeProps: any) => {
                                                                    const payload = shapeProps?.payload as ScatterEvent | undefined;
                                                                    const isHovered = getEventKey(payload) === hoveredEventKey;
                                                                    const sharedProps = {
                                                                        ...shapeProps,
                                                                        typeMeta,
                                                                        onSelect: onPointSelect,
                                                                    };

                                                                    return isHovered
                                                                        ? <EventScatterActiveShape {...sharedProps} size={18} />
                                                                        : <EventScatterShape {...sharedProps} size={16} />;
                                                                }}
                                                            />
                                                        </ScatterChart>
                                                    </ResponsiveContainer>
                                                    {selectedPoint ? (
                                                        <div
                                                            ref={selectedTooltipRef}
                                                            className={styles.selectedTooltip}
                                                            style={{
                                                                left: selectedTooltipPosition.left,
                                                                top: selectedTooltipPosition.top,
                                                            }}
                                                        >
                                                            <EventScatterTooltip
                                                                event={selectedPoint.event}
                                                                typeMeta={typeMeta}
                                                            />
                                                        </div>
                                                    ) : null}
                                                    {selectedStepGroup ? (
                                                        <div
                                                            ref={stepGroupPopoverRef}
                                                            className={styles.stepGroupPopover}
                                                            style={{
                                                                left: stepGroupPopoverPosition.left,
                                                                top: stepGroupPopoverPosition.top,
                                                            }}
                                                        >
                                                            <div className={styles.stepGroupTitle}>
                                                                {selectedStepGroup.zones.length} {messages['executionSummaryGraph.stepGroup.title']}
                                                            </div>
                                                            {selectedStepGroup.zones.map((zone, index) => (
                                                                <div
                                                                    key={zone.id ?? `${zone.idxLabel}-${index}`}
                                                                    className={styles.stepGroupRow}
                                                                    onMouseEnter={() => setHighlightedZoneId(zone.id ?? null)}
                                                                    onMouseLeave={() => setHighlightedZoneId(current => current === zone.id ? null : current)}
                                                                >
                                                                    <span className={styles.stepGroupIdx}>{zone.idxLabel}</span>
                                                                    <span className={styles.stepGroupName}>{zone.label}</span>
                                                                    <span className={styles.stepGroupTime}>
                                                                        {timeFormatterDuration(Number(zone.start))} – {timeFormatterDuration(Number(zone.end))}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : null}
                                                </div>
                                            )}
                                        </CustomHorizontalScroller>
                                    </div>
                                </div>
                            )}
                        </div>
                    )
                }else{
                    return(
                        <div className={styles.noEvent}>
                            No Events Detected
                        </div>
                    )
                }
            }}
        </Observer>
    )
}